/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './AttachmentRevisions';

const AttachmentRevisionsManager: ComponentManagerFn<'AttachmentRevisions'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'AttachmentRevisions'>(
      platform,
      'AttachmentRevisions',
    ),
    type: 'AttachmentRevisions',
    name: 'Attachment Revisions',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      title: {
        label: 'Title',
        type: 'string',
      },
      attachmentId: {
        label: 'Attachment Id',
        type: 'expression',
        returnType: async () => 'string',
      },
      open: {
        label: 'Open',
        type: 'boolean',
      },
    },
    getDisplayText: (item) => item.props.attachmentId.value ?? '',
    category: 'Forms',
    icon: { iconName: 'paperclip' },
    description: `**Overview**<br/>
The Attachment Revisions component is a utility interface used to view the version history of a specific file.
When file versioning is enabled in your backend object store, multiple uploads to the same record do not overwrite the previous file; they stack as new versions. This component displays that stack, allowing users to see who updated the file, when it was updated, and access previous versions (e.g., "Contract v1" vs. "Contract v2").

**1. Core Configuration**<br/>
To display history, the component needs to know which file to look up.
* **Attachment Id:** An expression that resolves to the unique identifier (UID) of the attachment.
* **Usage:** You typically bind this to the fileUid of the currently selected record or AttachmentField (e.g., \`$.orders.contractFile.fileUid\`).
* **Title:** The header text displayed at the top of the revisions list (e.g., "Document History").

**2. Interaction & State**<br/>
* **Open:** A boolean property that controls the visibility of the revisions list.
* **Note:** While this can be controlled via a boolean expression, it is most commonly triggered via the specific "Open Attachment Revisions" action in a workflow or button click.
* **TriggerOnClick:** You can add this child component to execute logic when a user clicks on a specific revision row. (Use Case: Trigger a download of that specific version or restore it as the current active version).

**3. Best Practices**<br/>
* **Context:** This component is rarely used as a permanent fixture on a page. It is typically used inside a Popover or triggered to appear in a drawer when a user clicks a "View History" button next to an Attachment Field.`,
  };
};

export default AttachmentRevisionsManager;
